import { useEffect, useState } from 'react';
import { useT } from '../i18n/useT';
import {
  listSnippets,
  newSnippet,
  refreshCustomCss,
  sanitizeCss,
  saveSnippets,
  type CssSnippet,
} from '../lib/customCss';
import { Icon } from './Icon';

/**
 * Your own CSS, as snippets you can switch on and off.
 *
 * Edits apply as you type — CSS is only worth writing if you can see it — and
 * are saved with the same keystroke. What a snippet is not allowed to do (fetch
 * anything) is said before you write it, and named afterwards if you wrote it
 * anyway, rather than quietly disappearing.
 */
export function CustomCssSettings() {
  const t = useT();
  const [snippets, setSnippets] = useState<CssSnippet[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    void listSnippets().then((loaded) => {
      setSnippets(loaded);
      setSelectedId((current) => current ?? loaded[0]?.id ?? null);
    });
  }, []);

  const update = async (next: CssSnippet[]) => {
    setSnippets(next);
    await saveSnippets(next);
    await refreshCustomCss();
  };

  const selected = snippets?.find((snippet) => snippet.id === selectedId) ?? null;
  const removed = selected ? sanitizeCss(selected.css).removed : [];

  return (
    <div className="mb-setting mb-setting-block">
      <label>{t('css.title')}</label>
      <div className="mb-backup">
        <p className="mb-modal-note">{t('css.hint')}</p>

        {snippets && snippets.length > 0 && (
          <ul className="mb-css-list">
            {snippets.map((snippet) => (
              <li key={snippet.id} className={snippet.id === selectedId ? 'active' : ''}>
                <input
                  type="checkbox"
                  checked={snippet.enabled}
                  aria-label={t('css.enable', { name: snippet.name })}
                  onChange={(e) =>
                    void update(
                      snippets.map((s) =>
                        s.id === snippet.id ? { ...s, enabled: e.target.checked } : s,
                      ),
                    )
                  }
                />
                <input
                  className="mb-css-name"
                  value={snippet.name}
                  aria-label={t('css.name')}
                  onFocus={() => setSelectedId(snippet.id)}
                  onChange={(e) =>
                    void update(
                      snippets.map((s) =>
                        s.id === snippet.id ? { ...s, name: e.target.value } : s,
                      ),
                    )
                  }
                />
                <button
                  className="mb-btn"
                  aria-label={t('css.delete', { name: snippet.name })}
                  title={t('css.delete', { name: snippet.name })}
                  onClick={() => {
                    const next = snippets.filter((s) => s.id !== snippet.id);
                    if (snippet.id === selectedId) setSelectedId(next[0]?.id ?? null);
                    void update(next);
                  }}
                >
                  <Icon name="trash" size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mb-backup-row">
          <button
            className="mb-btn"
            onClick={() => {
              const created = newSnippet(t('css.untitled', { n: (snippets?.length ?? 0) + 1 }));
              setSelectedId(created.id);
              void update([...(snippets ?? []), created]);
            }}
          >
            {t('css.add')}
          </button>
        </div>

        {selected && (
          <>
            <textarea
              className="mb-css-editor"
              spellCheck={false}
              rows={8}
              value={selected.css}
              aria-label={t('css.editorLabel', { name: selected.name })}
              placeholder={t('css.placeholder')}
              onChange={(e) =>
                void update(
                  (snippets ?? []).map((s) =>
                    s.id === selected.id ? { ...s, css: e.target.value } : s,
                  ),
                )
              }
            />
            {removed.length > 0 && (
              <p className="mb-modal-note mb-import-error" role="status">
                {t('css.removed', {
                  what: removed.map((r) => r.text).join(', '),
                })}
              </p>
            )}
          </>
        )}

        <p className="mb-backup-last">{t('css.escape')}</p>
      </div>
    </div>
  );
}
